import { cloneDeep, get, isEqual } from 'lodash';
import isNumeric from 'validator/es/lib/isNumeric';
import {
  translatedError, alphaNumUnderscore, isDuplicate, startWithNumber,
} from '@/utils/errors';
import { hasKey, hasMinimumLength, resultsWithDates } from '@/utils/resultsHelper';
import uuidGenerator from '@/utils/uuidGenerator';
import { addUUIDsToArray, findWithUUID, removeUUIDsFromArray } from '@/utils/uuidHelper';
import fieldNames, { globalFieldLengths } from '@/constants/fields';
import validateDictionaryFields from '../fields/helper';

export const validateBasicDictionary = (
  description,
  dictionaries,
  dictionaryCode,
  dictionaryUUID,
  name,
  originalName,
  sourceName,
) => {
  const errors = [];
  const context = { entity: 'dictionary', dictionaryUUID };

  if (dictionaryCode && isNumeric(dictionaryCode)) {
    errors.push(translatedError(
      context,
      'save.dataset.validation.dictionary.code.title',
      'validation.non.numeric',
      [fieldNames.CODE],
    ));
  }

  if (dictionaryCode && !alphaNumUnderscore(dictionaryCode)) {
    errors.push(translatedError(
      context,
      'save.dataset.validation.dictionary.code.title',
      'validation.code.alphanumeric.underscore',
      [fieldNames.CODE],
    ));
  }

  if (dictionaryCode && dictionaryCode.length > globalFieldLengths.code) {
    errors.push(translatedError(
      context,
      'save.dataset.validation.dictionary.code.title',
      'validation.max.length',
      [fieldNames.CODE],
      { maximum: globalFieldLengths.code },
    ));
  }

  if (dictionaryCode && startWithNumber(dictionaryCode)) {
    errors.push(translatedError(
      context,
      'save.dataset.validation.dictionary.code.title',
      'validation.not.start.with.number',
      [fieldNames.CODE],
    ));
  }

  if (!hasMinimumLength(name)) {
    errors.push(translatedError(
      context,
      'save.dataset.validation.required.dictionary.name.title',
      'save.dataset.validation.required.dictionary.name',
      [fieldNames.NAME],
    ));
  }

  if (name && name.length > globalFieldLengths.name) {
    errors.push(translatedError(
      context,
      'save.dataset.validation.dictionary.name.title',
      'validation.max.length',
      [fieldNames.NAME],
      { maximum: globalFieldLengths.name },
    ));
  }

  if (name && name.toLowerCase() !== originalName.toLowerCase() && isDuplicate('name', name, dictionaries)) {
    errors.push(translatedError(
      context,
      'save.dataset.validation.dictionary.name.title',
      'save.dataset.validation.dictionary.not.unique',
      [fieldNames.NAME],
    ));
  }

  if (sourceName && sourceName.length > globalFieldLengths.sourceName) {
    errors.push(translatedError(
      context,
      'save.dataset.validation.dictionary.source.name.title',
      'validation.max.length',
      [fieldNames.SOURCE_NAME],
      { maximum: globalFieldLengths.sourceName },
    ));
  }

  if (description && description.length > globalFieldLengths.description) {
    errors.push(translatedError(
      context,
      'save.dataset.validation.dictionary.description.title',
      'validation.max.length',
      [fieldNames.DESCRIPTION],
      { maximum: globalFieldLengths.description },
    ));
  }

  return errors;
};

const validateDictionary = ({ fields, name, uuid }, errors) => {
  const context = { entity: 'dictionary', dictionaryUUID: uuid };
  if (name && name.length > globalFieldLengths.name) {
    errors.push(translatedError(
      context,
      'save.dataset.validation.dictionary.name.title',
      'validation.max.length',
      [fieldNames.NAME],
      { maximum: globalFieldLengths.name },
    ));
  }

  if (!hasMinimumLength(name)) {
    errors.push(translatedError(
      context,
      'save.dataset.validation.required.dictionary.name.title',
      'save.dataset.validation.required.dictionary.name',
      [fieldNames.NAME],
    ));
  }

  if (fields) {
    validateDictionaryFields({ fields, name, uuid }, errors);
  }
};

const validateDictionaries = (diff, { dictionaries }) => {
  const errors = [];
  diff.dictionaries.filter((dictionary) => !hasKey(dictionary, 'toBeDeleted'))
    .forEach((dictionary) => validateDictionary(dictionary, errors));

  if (dictionaries.length > 0 && !dictionaries.some(({ primary }) => primary)) {
    const error = translatedError(
      { entity: 'dictionaries' },
      'save.dataset.validation.required.dictionary.primary.title',
      'save.dataset.validation.required.dictionary.primary',
      [fieldNames.PRIMARY],
    );
    error.translation.links = true;
    errors.push(error);
  }

  return errors;
};

const newAndDeletedDictionaries = (current, previous) => {
  const dictionaries = [];
  let currentUUIDs = current.map((dictionary) => dictionary.uuid);
  const previousUUIDs = previous.map((dictionary) => dictionary.uuid);
  const newUUIDs = currentUUIDs.filter((uuid) => !previousUUIDs.includes(uuid));
  const deletedUUIDs = previousUUIDs.filter((uuid) => !currentUUIDs.includes(uuid));

  newUUIDs.forEach((newUuid) => {
    dictionaries.push(findWithUUID(current, newUuid));
    currentUUIDs = currentUUIDs.filter((uuid) => uuid !== newUuid);
  });

  deletedUUIDs.forEach((deletedUuid) => {
    const dictionary = findWithUUID(previous, deletedUuid);
    dictionaries.push({
      id: dictionary.id,
      uuid: deletedUuid,
      code: dictionary.code,
      toBeDeleted: true,
    });
    currentUUIDs = currentUUIDs.filter((uuid) => uuid !== deletedUuid);
  });

  return { UUIDs: currentUUIDs, dictionaries };
};

const mapDictionaries = (current, previous, UUIDs) => (
  UUIDs.map((uuid) => {
    const currentDictionaries = findWithUUID(current, uuid);
    const previousDictionaries = findWithUUID(previous, uuid);
    if (isEqual(currentDictionaries, previousDictionaries)) { return null; }

    return ({
      current: currentDictionaries,
      previous: previousDictionaries,
    });
  }).filter((dictionary) => dictionary !== null)
);

const dictionaryDiff = (previous, {
  id,
  uuid,
  code,
  name,
  primary,
  source_name: sourceName,
  description,
  fields,
  lookups,
}) => {
  const diff = {
    id, uuid, code, name, primary, source_name: sourceName, description,
  };
  if (diff.source_name === previous.source_name) {
    delete diff.source_name;
  }
  if (!isEqual(fields, previous.fields)
    || !isEqual(lookups, previous.lookups)) {
    diff.fields = fields;
    diff.lookups = lookups;
  }

  return diff;
};

const checkDictionariesForDifferences = (current, previous) => {
  const { UUIDs, dictionaries } = newAndDeletedDictionaries(current, previous);
  const differences = mapDictionaries(current, previous, UUIDs);
  const diff = differences.map((dictionary) => dictionaryDiff(dictionary.previous, dictionary.current));

  return [...dictionaries, ...diff];
};

export const dictionariesDiff = (current, previous) => {
  const dictionaries = isEqual(current, previous) ? [] : checkDictionariesForDifferences(current, previous);
  return dictionaries.length > 0 ? { dictionaries } : { dictionaries: null };
};

const sanitizedDictionary = (dictionary) => {
  const keys = ['updated_at', 'created_at', 'dataset_id', 'status'];
  if (!hasMinimumLength(dictionary.code)) keys.push('code');

  const copy = { ...dictionary };
  keys.forEach((key) => delete copy[key]);

  return copy;
};

const prepareDictionary = (dictionary) => {
  const copy = cloneDeep(dictionary);
  const lookups = copy && copy.lookups ? copy.lookups : {};
  const fields = copy && copy.fields ? copy.fields : [];

  return {
    copy,
    lookups,
    lookupsKeys: Object.keys(lookups),
    fields,
  };
};

const addUUIDsToDictionary = (dictionary) => {
  const {
    copy, lookups, fields, lookupsKeys,
  } = prepareDictionary(dictionary);

  copy.uuid = uuidGenerator.generate();
  copy.fields = addUUIDsToArray(fields);
  lookupsKeys.forEach((key) => { lookups[key].options = addUUIDsToArray(lookups[key].options); });

  return copy;
};

export const primarySelectedDictionary = (result) => ({
  ...resultsWithDates(result),
  selected: result.primary || false,
});

const removeUUIDsFromDictionary = (dictionary) => {
  const {
    copy, lookups, fields, lookupsKeys,
  } = prepareDictionary(dictionary);

  delete copy.uuid;
  if (fields.length > 0) {
    copy.fields = removeUUIDsFromArray(fields);
  }

  if (lookupsKeys.length > 0) {
    lookupsKeys.forEach((key) => { lookups[key].options = removeUUIDsFromArray(lookups[key].options); });
  }

  return copy;
};

export const addUUIDsToDictionaries = (dictionaries) => dictionaries.map(addUUIDsToDictionary);

export const removeUUIDsFromDictionaries = (dictionaries) => dictionaries.map(removeUUIDsFromDictionary);

export const sanitizedDictionaries = (dictionaries) => dictionaries.map(sanitizedDictionary);

export const uniqueDatasetCodes = (items) => (
  [...new Set(items.map((r) => get(r, 'datasets[0].code')))]);

export const dictionariesHaveChanged = ({ isDirty, dictionaries, copy }) => (
  isDirty.dictionaries && (dictionaries.length > 0 || copy.dictionaries.length > 0)
);

export default validateDictionaries;
